import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Save, Trash2, Calendar } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth';
import { cn, formatBRL } from '@/lib/utils';
import {
  TASK_TYPES,
  TASK_TYPE_LABELS,
  TASK_TYPE_HINTS,
  type TaskType,
} from '@parecer/shared';

interface PricingRowAPI {
  taskType: TaskType;
  basePrice: number;
  description?: string;
  subType?: string;
  estimatedDeliveryHours?: number;
  estimatedDurationMinutes?: number;
  accessDurationDays?: number;
  slaResponseHours?: number;
  messageLimit?: number;
  active: boolean;
}

/** Local form row — adds a stable client-side id for React keys + reorder. */
interface RowFE extends PricingRowAPI {
  id: string;
}

interface LawyerWithPricing {
  userId: string;
  name: string;
  bookingUrl?: string;
  pricing?: PricingRowAPI[];
}

const PLATFORM_FEE_PCT = 15;
const TASK_TYPE_LIST = Object.values(TASK_TYPES) as TaskType[];

/**
 * Per-taskType "delivery" UX. The internal storage uses three optional
 * numeric fields (`estimatedDeliveryHours`, `estimatedDurationMinutes`,
 * `accessDurationDays`), and this mapping says which one to render and how
 * to label it so the lawyer doesn't have to mentally convert. Defaults to
 * hours-until-delivery for contract / opinion / analysis types.
 */
type DeliveryUnit = 'hours' | 'minutes' | 'days';
const DELIVERY_UNIT: Record<TaskType, DeliveryUnit> = {
  contract_review_simple: 'hours',
  contract_review_complex: 'hours',
  contract_drafting: 'hours',
  written_opinion: 'hours',
  chat_consultation: 'days',
  call_consultation: 'minutes',
  diagnostic_meeting: 'minutes',
  preventive_advice: 'hours',
  strategic_consulting: 'hours',
  case_analysis: 'hours',
  family_law_service: 'hours',
  out_of_court_negotiation: 'hours',
};

const DELIVERY_LABEL: Record<DeliveryUnit, { label: string; helper: string; max: number }> = {
  hours: { label: 'Prazo até entrega (horas)', helper: 'Quantas horas até você devolver o serviço.', max: 720 },
  minutes: { label: 'Duração da sessão (minutos)', helper: 'Tempo padrão de cada encontro.', max: 480 },
  days: { label: 'Duração do acesso (dias)', helper: 'Por quantos dias o cliente pode usar o chat.', max: 365 },
};

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function clientTotal(basePrice: number): number {
  return Math.round(basePrice * (1 + PLATFORM_FEE_PCT / 100));
}

export function LawyerPrecos() {
  const userId = useAuthStore((s) => s.user?.userId);
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: lawyer, isLoading } = useQuery({
    queryKey: ['lawyer', userId, 'self-pricing'],
    queryFn: () => api.get<LawyerWithPricing>(`/lawyers/${userId}`),
    enabled: Boolean(userId),
  });

  const [rows, setRows] = useState<RowFE[]>([]);
  const [bookingUrl, setBookingUrl] = useState('');

  useEffect(() => {
    if (!lawyer) return;
    setBookingUrl(lawyer.bookingUrl ?? '');
    if (!lawyer.pricing) return;
    setRows(
      lawyer.pricing.map((p) => ({
        id: newRowId(),
        taskType: p.taskType,
        basePrice: Number(p.basePrice ?? 0),
        active: Boolean(p.active),
        ...(p.description ? { description: p.description } : {}),
        ...(p.subType ? { subType: p.subType } : {}),
        ...(p.estimatedDeliveryHours !== undefined
          ? { estimatedDeliveryHours: Number(p.estimatedDeliveryHours) }
          : {}),
        ...(p.estimatedDurationMinutes !== undefined
          ? { estimatedDurationMinutes: Number(p.estimatedDurationMinutes) }
          : {}),
        ...(p.accessDurationDays !== undefined
          ? { accessDurationDays: Number(p.accessDurationDays) }
          : {}),
        ...(p.slaResponseHours !== undefined
          ? { slaResponseHours: Number(p.slaResponseHours) }
          : {}),
        ...(p.messageLimit !== undefined ? { messageLimit: Number(p.messageLimit) } : {}),
      })),
    );
  }, [lawyer?.pricing, lawyer?.bookingUrl]);

  function patchRow(id: string, patch: Partial<RowFE>): void {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function addRow(taskType: TaskType): void {
    setRows((prev) => [
      ...prev,
      {
        id: newRowId(),
        taskType,
        basePrice: 0,
        active: true,
      },
    ]);
  }

  function removeRow(id: string): void {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  // Group rows by taskType for nicer rendering.
  const grouped = useMemo(() => {
    const m = new Map<TaskType, RowFE[]>();
    for (const r of rows) {
      if (!m.has(r.taskType)) m.set(r.taskType, []);
      m.get(r.taskType)!.push(r);
    }
    return m;
  }, [rows]);

  const savePricingMut = useMutation({
    mutationFn: (pricing: PricingRowAPI[]) => api.put('/lawyers/me/pricing', { pricing }),
    onSuccess: () => {
      toast({ variant: 'success', title: 'Tabela atualizada' });
      qc.invalidateQueries({ queryKey: ['lawyer', userId] });
    },
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao salvar',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  const saveProfileMut = useMutation({
    mutationFn: (body: { bookingUrl?: string }) => api.patch('/lawyers/me', body),
    onError: (err) =>
      toast({
        variant: 'danger',
        title: 'Falha ao salvar bookingUrl',
        description: err instanceof Error ? err.message : 'Erro inesperado',
      }),
  });

  function handleSave(): void {
    if (rows.length === 0) {
      toast({ variant: 'warning', title: 'Adicione ao menos um serviço' });
      return;
    }
    // Validate active rows
    const invalid = rows.filter((r) => r.active && (!r.basePrice || r.basePrice <= 0));
    if (invalid.length > 0) {
      toast({
        variant: 'danger',
        title: 'Preços inválidos',
        description: 'Todo serviço ativo precisa de preço > 0.',
      });
      return;
    }
    // Dedup defensively by (taskType + subType normalized).
    const seen = new Set<string>();
    const dedup: RowFE[] = [];
    for (const r of rows) {
      const key = `${r.taskType}::${(r.subType ?? '').trim().toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      dedup.push(r);
    }
    const payload: PricingRowAPI[] = dedup.map(({ id: _id, ...rest }) => {
      void _id;
      // Strip undefined keys for cleaner DDB writes
      const out: PricingRowAPI = {
        taskType: rest.taskType,
        basePrice: rest.basePrice,
        active: rest.active,
      };
      if (rest.description) out.description = rest.description;
      if (rest.subType) out.subType = rest.subType.trim();
      if (rest.estimatedDeliveryHours) out.estimatedDeliveryHours = rest.estimatedDeliveryHours;
      if (rest.estimatedDurationMinutes) out.estimatedDurationMinutes = rest.estimatedDurationMinutes;
      if (rest.accessDurationDays) out.accessDurationDays = rest.accessDurationDays;
      if (rest.slaResponseHours) out.slaResponseHours = rest.slaResponseHours;
      if (rest.messageLimit) out.messageLimit = rest.messageLimit;
      return out;
    });
    savePricingMut.mutate(payload);
    // Save bookingUrl separately (different endpoint).
    const trimmed = bookingUrl.trim();
    saveProfileMut.mutate(trimmed ? { bookingUrl: trimmed } : { bookingUrl: '' });
  }

  return (
    <AppShell role="lawyers">
      <div className="max-w-3xl">
        <h1 className="font-serif text-display-lg">Tabela de preços</h1>
        <p className="text-muted-foreground mt-2">
          Defina seus preços por serviço. Você pode ter <strong>várias linhas do mesmo tipo</strong>{' '}
          se cobra diferente por contexto (ex: revisão de locação ≠ revisão de honorários).
          Plataforma adiciona <strong>{PLATFORM_FEE_PCT}%</strong> em cima — você recebe o valor cheio.
        </p>

        {isLoading && <p className="text-sm text-muted-foreground mt-8">Carregando…</p>}

        {!isLoading && (
          <div className="mt-6 space-y-6">
            {/* Booking URL — only relevant for lawyers who offer call/meeting types */}
            <Card>
              <CardHeader className="py-5">
                <CardTitle className="text-base flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-accent" /> Agenda online (opcional)
                </CardTitle>
                <CardDescription>
                  Cole o link da sua agenda (Calendly, Cal.com, TidyCal…). Aparece no card de call /
                  reunião pro cliente reservar horário direto. Integração nativa com Daily.co e
                  Google Calendar fica pra fase 2.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Input
                  type="url"
                  placeholder="https://calendly.com/seu-nome/consulta"
                  value={bookingUrl}
                  onChange={(e) => setBookingUrl(e.target.value)}
                />
              </CardContent>
            </Card>

            {/* Existing rows grouped by type */}
            {TASK_TYPE_LIST.map((t) => {
              const list = grouped.get(t) ?? [];
              const unit = DELIVERY_UNIT[t];
              const isChat = t === TASK_TYPES.CHAT_CONSULTATION;
              const isCallish = unit === 'minutes';

              return (
                <Card key={t}>
                  <CardHeader className="py-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-base">{TASK_TYPE_LABELS[t]}</CardTitle>
                        <CardDescription>{TASK_TYPE_HINTS[t].description}</CardDescription>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => addRow(t)}
                        className="gap-1"
                      >
                        <Plus className="h-3.5 w-3.5" /> Linha
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {list.length === 0 && (
                      <p className="text-sm text-muted-foreground italic">
                        Nenhuma linha pra esse serviço.{' '}
                        <button
                          type="button"
                          onClick={() => addRow(t)}
                          className="text-accent hover:underline"
                        >
                          Adicionar uma →
                        </button>
                      </p>
                    )}
                    {list.map((row) => (
                      <div
                        key={row.id}
                        className={cn(
                          'rounded-md border p-4 space-y-3',
                          row.active ? 'border-accent/40 bg-accent/[0.02]' : 'border-border/60 bg-muted/10',
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <ToggleSwitch
                            checked={row.active}
                            onChange={(v) => patchRow(row.id, { active: v })}
                            ariaLabel={`Ativar ${TASK_TYPE_LABELS[t]}`}
                          />
                          <div className="flex-1 min-w-0">
                            <Input
                              placeholder="Subtipo (opcional) — ex: Locação residencial, Honorários trabalhistas, M&A"
                              value={row.subType ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                patchRow(row.id, (v ? { subType: v } : { subType: undefined }) as Partial<RowFE>);
                              }}
                              className="text-sm"
                            />
                          </div>
                          {row.active && row.basePrice > 0 && (
                            <Badge variant="accent" className="flex-none">
                              {formatBRL(row.basePrice)}
                            </Badge>
                          )}
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-danger hover:text-danger flex-none"
                            onClick={() => removeRow(row.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {row.active && (
                          <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                              <Label>Preço (R$)</Label>
                              <Input
                                type="number"
                                inputMode="decimal"
                                min={0}
                                step="0.01"
                                placeholder="500,00"
                                className="mt-1"
                                value={row.basePrice ? (row.basePrice / 100).toFixed(2) : ''}
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value || '0');
                                  patchRow(row.id, {
                                    basePrice: Number.isFinite(v) ? Math.round(v * 100) : 0,
                                  });
                                }}
                              />
                              <p className="text-[11px] text-muted-foreground mt-1">
                                Cliente vê {formatBRL(clientTotal(row.basePrice))} (você recebe{' '}
                                {formatBRL(row.basePrice)})
                              </p>
                            </div>

                            {/* Per-type delivery field */}
                            <div>
                              <Label>{DELIVERY_LABEL[unit].label}</Label>
                              <Input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={DELIVERY_LABEL[unit].max}
                                step={1}
                                placeholder={
                                  unit === 'hours' ? '72' : unit === 'minutes' ? '60' : '7'
                                }
                                className="mt-1"
                                value={
                                  unit === 'hours'
                                    ? row.estimatedDeliveryHours ?? ''
                                    : unit === 'minutes'
                                      ? row.estimatedDurationMinutes ?? ''
                                      : row.accessDurationDays ?? ''
                                }
                                onChange={(e) => {
                                  const v = parseInt(e.target.value || '0', 10);
                                  const num = Number.isFinite(v) && v > 0 ? v : undefined;
                                  if (unit === 'hours') {
                                    patchRow(row.id, { estimatedDeliveryHours: num } as Partial<RowFE>);
                                  } else if (unit === 'minutes') {
                                    patchRow(row.id, { estimatedDurationMinutes: num } as Partial<RowFE>);
                                  } else {
                                    patchRow(row.id, { accessDurationDays: num } as Partial<RowFE>);
                                  }
                                }}
                              />
                              <p className="text-[11px] text-muted-foreground mt-1">
                                {DELIVERY_LABEL[unit].helper}
                              </p>
                            </div>

                            {/* Chat-specific extra fields */}
                            {isChat && (
                              <>
                                <div>
                                  <Label>Respondo em até (horas)</Label>
                                  <Input
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    max={168}
                                    placeholder="24"
                                    className="mt-1"
                                    value={row.slaResponseHours ?? ''}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value || '0', 10);
                                      patchRow(row.id, {
                                        slaResponseHours: Number.isFinite(v) && v > 0 ? v : undefined,
                                      } as Partial<RowFE>);
                                    }}
                                  />
                                  <p className="text-[11px] text-muted-foreground mt-1">
                                    Compromisso visível pro cliente. Quebra de SLA → mediação.
                                  </p>
                                </div>
                                <div>
                                  <Label>Limite de mensagens (opcional)</Label>
                                  <Input
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    max={10000}
                                    placeholder="50"
                                    className="mt-1"
                                    value={row.messageLimit ?? ''}
                                    onChange={(e) => {
                                      const v = parseInt(e.target.value || '0', 10);
                                      patchRow(row.id, {
                                        messageLimit: Number.isFinite(v) && v > 0 ? v : undefined,
                                      } as Partial<RowFE>);
                                    }}
                                  />
                                </div>
                              </>
                            )}

                            {/* Booking URL hint pra call/meeting */}
                            {isCallish && (
                              <div className="sm:col-span-2 rounded-md border border-dashed border-border/80 bg-muted/20 p-3 text-xs flex items-start gap-2">
                                <Calendar className="h-3.5 w-3.5 mt-0.5 flex-none text-muted-foreground" />
                                <p className="text-muted-foreground">
                                  {bookingUrl
                                    ? `Cliente verá um botão "Agendar" apontando pra ${bookingUrl}.`
                                    : 'Sem agenda online configurada. Cliente vai falar com você por chat pra marcar — preencha o campo "Agenda online" no topo.'}
                                </p>
                              </div>
                            )}

                            <div className="sm:col-span-2">
                              <Label>Descrição (opcional)</Label>
                              <textarea
                                rows={2}
                                maxLength={500}
                                placeholder="Detalhe escopo, limites, o que está incluso..."
                                className="mt-1 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                value={row.description ?? ''}
                                onChange={(e) =>
                                  patchRow(row.id, {
                                    description: e.target.value || undefined,
                                  } as Partial<RowFE>)
                                }
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}

            <div className="sticky bottom-4 flex items-center justify-between gap-3 bg-background/80 backdrop-blur border border-border/60 rounded-lg p-3 shadow-sm">
              <p className="text-xs text-muted-foreground flex-1">
                Mudanças ficam públicas assim que você salvar. Linhas que você remover
                somem do seu card. {rows.length} linha(s) no total.
              </p>
              <Button
                variant="accent"
                onClick={handleSave}
                loading={savePricingMut.isPending}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                Salvar tabela
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors focus-ring',
        checked ? 'bg-accent' : 'bg-muted',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-card shadow-sm transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}
